import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import BrandLogo from '../brand/BrandLogo';
import NodeField from '../brand/NodeField';
import useBrandTheme from '../brand/useBrandTheme';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Shared shell for the public auth pages (login / signup / forgot password / invites),
 * in the LatentForce brand: paper ground in light mode, ink in dark, over the
 * hover-reactive node field, with the floating header bar from the landing page.
 * The form sits in a compact card: heading, fields, and a footer strip for the
 * "other way in" link.
 *
 * `onBack` — on a multi-step flow (e.g. the reset-code screen), shows a Back link
 * above the heading that steps back within the flow.
 */
function AuthLayout({ children, title, subtitle, footer, onBack }) {
  useBrandTheme();
  const { theme } = useTheme();
  return (
    <NodeField
      className="min-h-screen bg-surface text-fg"
      spacing={40}
      lineColor={theme === 'dark' ? '240, 238, 232' : '44, 49, 35'}
    >
      {/* Floating header bar */}
      <header className="sticky top-4 z-10 px-4 sm:px-5">
        <div className="max-w-[1240px] mx-auto flex items-center gap-4 rounded-[20px] border border-panel-border bg-panel/90 backdrop-blur-xl px-4 sm:px-5 py-2.5 shadow-[0_1px_0_rgba(34,38,28,0.10)]">
          <Link to="/" className="flex" aria-label="LatentStack home">
            <BrandLogo height={19} />
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/docs"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-medium text-fg-muted hover:text-fg hover:bg-surface-2 transition-colors"
            >
              <BookOpen className="w-3.5 h-3.5" />
              Docs
            </Link>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="flex justify-center px-4 pt-[calc(3.5rem+10vh)] pb-16 sm:pt-[calc(5rem+10vh)]">
        <div className="w-full max-w-[400px]">
          <div className="overflow-hidden rounded-[20px] border border-panel-border bg-panel shadow-[0_1px_0_rgba(8,9,10,0.06),0_24px_48px_-28px_rgba(34,38,28,0.35)]">
            <div className="px-7 sm:px-8 pt-7 pb-6">
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  aria-label="Back"
                  className="mb-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-fg-muted hover:text-fg transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back
                </button>
              )}
              <h1 className="font-display text-[26px] leading-[1.15] font-medium tracking-[-0.018em]">{title}</h1>
              {subtitle && <p className="mt-1.5 text-sm text-fg-muted">{subtitle}</p>}
            </div>
            <div className="px-7 sm:px-8 pb-7">{children}</div>
            {footer && (
              <div className="border-t border-panel-border bg-surface-2/70 px-7 sm:px-8 py-4 text-center text-[13px] text-fg-muted">
                {footer}
              </div>
            )}
          </div>
          <p className="mt-5 text-center font-mono text-[11px] uppercase tracking-[0.14em] text-fg-muted/80">
            Your infrastructure · your data stays in
          </p>
        </div>
      </main>
    </NodeField>
  );
}

/** Shared field/label/button primitives so the auth pages stay consistent. */
export const labelClass = 'block text-[13px] font-medium text-fg mb-1.5';

export function Field({ label, hint, children }) {
  return (
    <div className="mb-4">
      <label className={labelClass}>{label}</label>
      {children}
      {hint && <p className="mt-1.5 text-xs text-fg-muted">{hint}</p>}
    </div>
  );
}

export const inputClass =
  'w-full h-11 px-3.5 bg-field border border-field-border rounded-[10px] text-sm text-fg placeholder-fg-muted/60 focus:border-orange focus:ring-2 focus:ring-orange/15 transition-colors';

export const primaryBtnClass =
  'w-full h-11 bg-orange hover:bg-orange-hover text-white text-sm font-medium rounded-[10px] transition-colors active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed';

export function Alert({ kind = 'error', children }) {
  if (!children) return null;
  const styles =
    kind === 'error'
      ? 'bg-[#F0421F]/10 border-[#F0421F]/25 text-[#C4321A] dark:text-[#F7795F]'
      : kind === 'success'
      ? 'bg-[#57CC88]/10 border-[#57CC88]/30 text-[#2F6B4A] dark:text-[#57CC88]'
      : 'bg-orange/10 border-orange/25 text-orange';
  return <div className={`mb-4 px-3.5 py-2.5 rounded-[10px] border text-[13px] ${styles}`}>{children}</div>;
}

export default AuthLayout;
